/**
 * Sign API Service
 *
 * Frontend wrapper for Platform.Sign API endpoints.
 * All endpoints require authentication except signing ceremony endpoints.
 */
import api from './api';

/**
 * Package Management API
 */
export const packages = {
    /**
     * List packages with optional filtering and pagination
     * @param {Object} params - Query parameters
     * @param {number} [params.page] - Page number (1-based)
     * @param {number} [params.pageSize] - Items per page (max 100)
     * @param {string} [params.status] - Filter by status (Draft, Sent, Viewed, InProgress, Completed, Voided, Expired)
     * @param {Date} [params.createdAfter] - Filter packages created after this date
     * @param {Date} [params.createdBefore] - Filter packages created before this date
     * @param {string} [params.search] - Search by package name
     * @returns {Promise<{items: Array, totalCount: number, pageNumber: number, pageSize: number}>}
     */
    list(params = {}) {
        return api.get('/packages', { params });
    },

    /**
     * Get a package by ID
     * @param {string} packageId - Package GUID
     * @returns {Promise<Object>} Package details
     */
    get(packageId) {
        return api.get(`/packages/${packageId}`);
    },

    /**
     * Create a new signing package
     * @param {Object} data - Package data
     * @param {string} data.name - Package name (required)
     * @param {string} [data.emailSubject] - Email subject for notifications
     * @param {string} [data.emailMessage] - Email message body
     * @param {boolean} [data.enforceSigningOrder] - Whether to enforce sequential signing
     * @param {Date} [data.expirationDate] - Package expiration date
     * @param {boolean} [data.requiresPayment] - Whether payment is required
     * @param {number} [data.paymentAmount] - Payment amount in cents
     * @param {string} [data.paymentCurrency] - Payment currency (default: usd)
     * @param {string} [data.paymentDescription] - Payment description
     * @returns {Promise<Object>} Created package
     */
    create(data) {
        return api.post('/packages', data);
    },

    /**
     * Create a package with signers and documents, then send or schedule it atomically.
     * Documents are referenced by content hash from a prior upload.
     * @param {Object} data - Package, signers, and document references
     * @returns {Promise<Object>} Created and sent package
     */
    createAndSend(data) {
        return api.post('/packages/create-and-send', data);
    },

    /**
     * Update a draft package
     * @param {string} packageId - Package GUID
     * @param {Object} data - Fields to update
     * @returns {Promise<Object>} Updated package
     */
    update(packageId, data) {
        return api.put(`/packages/${packageId}`, data);
    },

    /**
     * Delete a draft package
     * @param {string} packageId - Package GUID
     * @returns {Promise<void>}
     */
    delete(packageId) {
        return api.delete(`/packages/${packageId}`);
    },

    /**
     * Bulk delete draft packages
     * @param {string[]} ids - Array of package GUIDs
     * @returns {Promise<{results: Array<{id: string, success: boolean, error: string?}>}>}
     */
    bulkDelete(ids) {
        return api.post('/packages/bulk-delete', { ids });
    },

    /**
     * Validate a package's data integrity and preconditions before sending.
     * @param {string} packageId - Package GUID
     * @returns {Promise<{isValid: boolean, errors: Array<{errorCode: string, field: string, message: string}>}>}
     */
    validate(packageId) {
        return api.post(`/packages/${packageId}/validate`);
    },

    /**
     * Read-only preview of the credit consumption + auto-refill that will fire if this package
     * is sent right now. Use this to render a cost summary above the Send button.
     * @param {string} packageId - Package GUID
     * @returns {Promise<{types: Array<{type: string, neededForPackage: number, freeQuotaAvailable: number, prepaidAvailable: number, shortfall: number, autoRefillConfigured: boolean, autoRefillPacks: number, autoRefillCost: number, refillLargerThanUsual: boolean}>, totalNewCharges: number, currency: string}>}
     */
    getSendImpact(packageId) {
        return api.get(`/packages/${packageId}/send-impact`);
    },

    /**
     * Send a package for signing
     * @param {string} packageId - Package GUID
     * @param {Object} [options] - Send options
     * @param {Date} [options.scheduledSendTime] - Schedule send for later
     * @returns {Promise<Object>} Updated package
     */
    send(packageId, options = null) {
        return api.post(`/packages/${packageId}/send`, options);
    },

    /**
     * Step 1 of the Free-tier per-package pre-payment flow.
     * Creates a one-time Stripe PaymentIntent for the per-package charge.
     * Only valid for Free-tier tenants (MonthlyPrice == 0) with a per-package overage
     * price configured. Other tiers must configure a saved payment method.
     *
     * countryCode + postalCode are REQUIRED so the server can compute Stripe Tax
     * before the PaymentIntent is created (the card hasn't been collected yet so
     * we can't derive the address from billing_details - the dialog collects
     * these up front).
     *
     * @param {string} packageId - Package GUID (must be in Draft status)
     * @param {{countryCode: string, postalCode: string}} billing - Billing country (ISO-2) + postal code for tax calc
     * @returns {Promise<{
     *   paymentIntentId: string,
     *   clientSecret: string,
     *   amount: number,
     *   taxAmount: number,
     *   totalAmount: number,
     *   taxJurisdiction: string | null,
     *   currency: string,
     *   description: string,
     *   publishableKey: string
     * }>}
     */
    createPrePayIntent(packageId, billing) {
        return api.post(`/packages/${packageId}/prepay/create-intent`, billing);
    },

    /**
     * Step 2 of the Free-tier per-package pre-payment flow.
     * After the client has confirmed the PaymentIntent via Stripe Elements, this endpoint
     * verifies the PI server-side, writes the ledger rows, and sends the package.
     * Idempotent: retrying after a transient send failure will NOT double-charge.
     * @param {string} packageId - Package GUID
     * @param {string} paymentIntentId - The PI returned by createPrePayIntent and confirmed by stripe.confirmPayment
     * @returns {Promise<{packageId: string, status: string, signPaymentId: number}>}
     */
    confirmPrePayAndSend(packageId, paymentIntentId) {
        return api.post(`/packages/${packageId}/prepay/confirm-and-send`, { paymentIntentId });
    },

    /**
     * Free-tier saved-PM overage confirm-and-send.
     *
     * Called when a Free-tier tenant clicks Confirm in the overage dialog after a
     * regular `/send` returned 402 OverageConsentRequired. The server runs the
     * actual off-session charge against the saved card, sets OverageIncurredOn,
     * and sends the package with bypassOverageDraw=true.
     *
     * No client-side Stripe Elements are involved - the saved PM lives on the
     * Stripe customer, and the user's confirmation is the consent boundary.
     *
     * @param {string} packageId - Package GUID (must be in Draft after the prior send reverted)
     * @returns {Promise<Object>} Updated package
     */
    confirmOverageAndSend(packageId) {
        return api.post(`/packages/${packageId}/overage/confirm-and-send`);
    },

    /**
     * Void a package
     * @param {string} packageId - Package GUID
     * @param {string} [reason] - Void reason
     * @returns {Promise<Object>} Updated package
     */
    void(packageId, reason = null) {
        return api.post(`/packages/${packageId}/void`, { reason });
    },

    /**
     * Cancel a scheduled send, reverting the package to Draft.
     * @param {string} packageId - Package GUID
     * @returns {Promise<Object>} Updated package
     */
    cancelSchedule(packageId) {
        return api.post(`/packages/${packageId}/cancel-schedule`);
    },

    /**
     * Update reminder settings on an in-flight package
     * @param {string} packageId - Package GUID
     * @param {Object} settings - { reminderEnabled: boolean, reminderIntervalDays?: number }
     * @returns {Promise<Object>} Updated package
     */
    updateReminders(packageId, settings) {
        return api.patch(`/packages/${packageId}/reminders`, settings);
    },

    /**
     * Resend notification emails to pending signers
     * @param {string} packageId - Package GUID
     * @returns {Promise<void>}
     */
    resend(packageId) {
        return api.post(`/packages/${packageId}/resend`);
    },

    /**
     * Create a new draft copy of a Declined, Voided, or Expired package
     * @param {string} packageId - Source package GUID
     * @param {string} [name] - New package name (defaults to original + " (Copy)")
     * @returns {Promise<Object>} New draft package
     */
    clone(packageId, name = null) {
        return api.post(`/packages/${packageId}/clone`, { name });
    },

    /**
     * Get download URL for completed package
     * @param {string} packageId - Package GUID
     * @returns {Promise<{url: string, expiresOn: string}>}
     */
    getDownloadUrl(packageId) {
        return api.get(`/packages/${packageId}/download`);
    },

    /**
     * Get a download URL for the Certificate of Completion
     * @param {string} packageId - Package GUID
     * @returns {Promise<{url: string, expiresOn: string}>}
     */
    getCertificateUrl(packageId) {
        return api.get(`/packages/${packageId}/certificate`);
    },

    /**
     * Get a download URL for a signer-uploaded attachment
     */
    getAttachmentUrl(packageId, annotationId) {
        return api.get(`/packages/${packageId}/annotations/${annotationId}/attachment`);
    },

    /**
     * Archive a terminal package (Completed, Declined, Voided, Expired)
     * @param {string} packageId - Package GUID
     * @returns {Promise<Object>} Updated package
     */
    archive(packageId) {
        return api.post(`/packages/${packageId}/archive`);
    },

    /**
     * Restore an archived package to the active list
     * @param {string} packageId - Package GUID
     * @returns {Promise<Object>} Updated package
     */
    unarchive(packageId) {
        return api.post(`/packages/${packageId}/unarchive`);
    },

    /**
     * Archive multiple terminal packages
     * @param {string[]} ids - Array of package GUIDs
     * @returns {Promise<{results: Array<{id: string, success: boolean, error: string?}>}>}
     */
    bulkArchive(ids) {
        return api.post('/packages/bulk-archive', { ids });
    },

    /**
     * Get audit trail events for a package
     * @param {string} packageId - Package GUID
     * @returns {Promise<Array<{eventType: string, description: string, timestamp: string, actorEmail: string?, actorIpAddress: string?, details: string?, signerGuid: string?, documentGuid: string?, annotationGuid: string?}>>}
     */
    getAuditTrail(packageId) {
        return api.get(`/packages/${packageId}/audit-trail`);
    },

    /**
     * Export audit trail as JSON, CSV, or PDF file download
     * @param {string} packageId - Package GUID
     * @param {string} [format='json'] - Export format: 'json', 'csv', or 'pdf'
     * @returns {Promise<Blob>}
     */
    exportAuditTrail(packageId, format = 'json') {
        return api.get(`/packages/${packageId}/audit-trail/export`, {
            params: { format },
            responseType: 'blob'
        });
    },

    /**
     * Verify the integrity of a package's audit trail hash chain
     * @param {string} packageId - Package GUID
     * @returns {Promise<{sqlChainValid: boolean, blobChainValid: boolean, sqlBlobMatch: boolean, sqlEventCount: number, blobEventCount: number, errorMessage: string?}>}
     */
    verifyAuditIntegrity(packageId) {
        return api.get(`/packages/${packageId}/audit-trail/verify`);
    }
};

/**
 * Document Management API
 */
export const documents = {
    /**
     * List documents in a package
     * @param {string} packageId - Package GUID
     * @returns {Promise<Array>} List of documents
     */
    list(packageId) {
        return api.get(`/packages/${packageId}/documents`);
    },

    /**
     * Get a document by ID
     * @param {string} packageId - Package GUID
     * @param {string} documentId - Document GUID
     * @returns {Promise<Object>} Document details
     */
    get(packageId, documentId) {
        return api.get(`/packages/${packageId}/documents/${documentId}`);
    },

    /**
     * Delete a document from a package
     * @param {string} packageId - Package GUID
     * @param {string} documentId - Document GUID
     * @returns {Promise<void>}
     */
    delete(packageId, documentId) {
        return api.delete(`/packages/${packageId}/documents/${documentId}`);
    },

    /**
     * Preview the impact of removing a document (orphaned signers)
     * @param {string} packageId - Package GUID
     * @param {string} documentId - Document GUID
     * @returns {Promise<{documentId: string, orphanedSigners: Array}>}
     */
    getRemovalImpact(packageId, documentId) {
        return api.get(`/packages/${packageId}/documents/${documentId}/removal-impact`);
    },

    /**
     * Detect text tags in a document (re-processes from scratch)
     * @param {string} packageId - Package GUID
     * @param {string} documentId - Document GUID
     * @param {Object} [options] - Detection options
     * @param {string} [options.format] - Tag format (DocuSign, AdobeSign, Custom)
     * @returns {Promise<{detectedFormat: string, tags: Array, unrecognizedTagCount: number}>}
     */
    detectTags(packageId, documentId, options = null) {
        return api.post(`/packages/${packageId}/documents/${documentId}/detect-tags`, options);
    },

    /**
     * Get previously detected text tags from cache (no re-processing)
     * @param {string} packageId - Package GUID
     * @param {string} documentId - Document GUID
     * @returns {Promise<{detectedFormat: string, tags: Array, unrecognizedTagCount: number}>}
     */
    getDetectedTags(packageId, documentId) {
        return api.get(`/packages/${packageId}/documents/${documentId}/detected-tags`);
    },

    /**
     * Get download URL for a document
     * @param {string} packageId - Package GUID
     * @param {string} documentId - Document GUID
     * @param {string} [type='pdf'] - Download type: 'pdf', 'signed', or 'original'
     * @returns {Promise<{url: string, expiresOn: string}>}
     */
    getDownloadUrl(packageId, documentId, type = 'pdf') {
        return api.get(`/packages/${packageId}/documents/${documentId}/download`, {
            params: { type }
        });
    },

    /**
     * Get page images for a document (for the visual editor and signing viewer).
     * Each page now also returns medianLineHeightPt - the document's body-text line
     * height extracted from the PDF text layer at upload time. The field editor
     * uses this to scale type-aware default annotation sizes at drop time so the
     * UI matches what the server resolver would produce. Null on scanned/image-only
     * pages - fall back to a fraction of pageHeight (see annotationFieldTypes API).
     * @param {string} packageId - Package GUID
     * @param {string} documentId - Document GUID
     * @param {number} [dpi=144] - Image DPI (72 for thumbnails, 144 for editor)
     * @returns {Promise<{documentId: string, dpi: number, pages: Array<{pageNumber: number, widthPt: number, heightPt: number, imageUrl: string, medianLineHeightPt: number|null}>}>}
     */
    getPages(packageId, documentId, dpi = 144) {
        return api.get(`/packages/${packageId}/documents/${documentId}/pages`, {
            params: { dpi }
        });
    },

    /**
     * Chunked upload methods for large files
     */
    chunkedUpload: {
        /**
         * Initiate a chunked upload session
         * @param {string} packageId - Package GUID
         * @param {string} fileName - Original file name
         * @param {number} totalSizeBytes - Total file size in bytes
         * @returns {Promise<{sessionId: string, maxBlockSizeBytes: number, expiresOn: string}>}
         */
        initiate(packageId, fileName, totalSizeBytes) {
            return api.post(`/packages/${packageId}/uploads`, { fileName, totalSizeBytes });
        },

        /**
         * Stage a block (chunk) of the file
         * @param {string} packageId - Package GUID
         * @param {string} sessionId - Upload session GUID
         * @param {number} blockIndex - Zero-based block index
         * @param {ArrayBuffer|Blob} blockData - Raw block data
         * @returns {Promise<{sessionId: string, blockIndex: number, totalBlocks: number}>}
         */
        stageBlock(packageId, sessionId, blockIndex, blockData) {
            return api.put(
                `/packages/${packageId}/uploads/${sessionId}/blocks/${blockIndex}`,
                blockData,
                { headers: { 'Content-Type': 'application/octet-stream' }, timeout: 120000 }
            );
        },

        /**
         * Commit an upload session (assembles all blocks into a document)
         * @param {string} packageId - Package GUID
         * @param {string} sessionId - Upload session GUID
         * @param {Object} [options] - Commit options
         * @param {string} [options.displayName] - Document display name
         * @param {number} [options.displayOrder] - Document display order
         * @returns {Promise<Object>} Created document info
         */
        commit(packageId, sessionId, options = {}) {
            return api.post(`/packages/${packageId}/uploads/${sessionId}/commit`, options);
        },

        /**
         * Abort an upload session and clean up staged blocks
         * @param {string} packageId - Package GUID
         * @param {string} sessionId - Upload session GUID
         * @returns {Promise<void>}
         */
        abort(packageId, sessionId) {
            return api.delete(`/packages/${packageId}/uploads/${sessionId}`);
        },

        /**
         * Get the status of an upload session
         * @param {string} packageId - Package GUID
         * @param {string} sessionId - Upload session GUID
         * @returns {Promise<Object>} Session status
         */
        getStatus(packageId, sessionId) {
            return api.get(`/packages/${packageId}/uploads/${sessionId}`);
        }
    }
};

/**
 * Signer Management API
 */
export const signers = {
    /**
     * List signers in a package
     * @param {string} packageId - Package GUID
     * @returns {Promise<Array>} List of signers
     */
    list(packageId) {
        return api.get(`/packages/${packageId}/signers`);
    },

    /**
     * Get a signer by ID
     * @param {string} packageId - Package GUID
     * @param {string} signerId - Signer GUID
     * @returns {Promise<Object>} Signer details
     */
    get(packageId, signerId) {
        return api.get(`/packages/${packageId}/signers/${signerId}`);
    },

    /**
     * Add a signer to a package
     * @param {string} packageId - Package GUID
     * @param {Object} data - Signer data
     * @param {string} data.email - Signer email (required)
     * @param {string} data.name - Signer name (required)
     * @param {string} [data.type] - Signer type (Signer, CarbonCopy, CertifiedDelivery, Approver, Editor)
     * @param {number} [data.routingOrder] - Signing order (default: 1)
     * @param {string} [data.authMethod] - Auth method (Email, SMS, AccessCode, IDVerification)
     * @param {string} [data.phone] - Phone for SMS auth
     * @param {string} [data.accessCode] - Access code for AccessCode auth
     * @param {string} [data.clientUserId] - Client user ID for embedded signing
     * @param {number} [data.signingGroupId] - Signing group ID
     * @returns {Promise<Object>} Created signer
     */
    add(packageId, data) {
        return api.post(`/packages/${packageId}/signers`, data);
    },

    /**
     * Add multiple signers to a package in a single atomic operation
     * @param {string} packageId - Package GUID
     * @param {Array<Object>} signers - Array of signer data objects
     * @returns {Promise<{signers: Array, count: number}>}
     */
    addBulk(packageId, signers) {
        return api.post(`/packages/${packageId}/signers/bulk`, { signers });
    },

    /**
     * Update a signer
     * @param {string} packageId - Package GUID
     * @param {string} signerId - Signer GUID
     * @param {Object} data - Fields to update
     * @returns {Promise<Object>} Updated signer
     */
    update(packageId, signerId, data) {
        return api.put(`/packages/${packageId}/signers/${signerId}`, data);
    },

    /**
     * Remove a signer from a package
     * @param {string} packageId - Package GUID
     * @param {string} signerId - Signer GUID
     * @returns {Promise<void>}
     */
    remove(packageId, signerId) {
        return api.delete(`/packages/${packageId}/signers/${signerId}`);
    },

    /**
     * Get a signing URL for embedded signing
     * @param {string} packageId - Package GUID
     * @param {string} signerId - Signer GUID
     * @param {Object} [options] - URL options
     * @param {string} [options.returnUrl] - URL to redirect to after signing
     * @param {number} [options.expirationMinutes] - URL expiration in minutes
     * @returns {Promise<{url: string, expiresOn: string}>}
     */
    getSigningUrl(packageId, signerId, options = null) {
        return api.post(`/packages/${packageId}/signers/${signerId}/signing-url`, options);
    },

    /**
     * Resend notification email to a signer
     * @param {string} packageId - Package GUID
     * @param {string} signerId - Signer GUID
     * @returns {Promise<void>}
     */
    resend(packageId, signerId) {
        return api.post(`/packages/${packageId}/signers/${signerId}/resend`);
    },

    /**
     * Resend notification emails to multiple signers
     * @param {string} packageId - Package GUID
     * @param {Array<string>} signerIds - Array of signer GUIDs
     * @returns {Promise<{count: number}>}
     */
    resendBulk(packageId, signerIds) {
        return api.post(`/packages/${packageId}/signers/resend-bulk`, { signerIds });
    },

    /**
     * Reorder signers in a draft package (batch update of routing orders)
     * @param {string} packageId - Package GUID
     * @param {Array<{signerId: string, routingOrder: number}>} signers - New order
     * @returns {Promise<{signers: Array, count: number}>}
     */
    reorder(packageId, signers) {
        return api.put(`/packages/${packageId}/signers/reorder`, { signers });
    },

    /**
     * Reset KBA verification for a signer (creator operation).
     * Grants fresh quiz attempts and reserves new credits.
     * @param {string} packageId - Package GUID
     * @param {string} signerId - Signer GUID
     * @returns {Promise<{success: boolean, creditsReserved: number, message?: string}>}
     */
    resetKba(packageId, signerId, { maxAttempts } = {}) {
        return api.post(`/packages/${packageId}/signers/${signerId}/reset-kba`, { maxAttempts });
    },

    /**
     * Reset ID verification for a signer (creator operation).
     * Grants a fresh verification attempt and reserves new credits.
     * @param {string} packageId - Package GUID
     * @param {string} signerId - Signer GUID
     * @returns {Promise<{success: boolean, creditsReserved: number, message?: string}>}
     */
    resetIdv(packageId, signerId) {
        return api.post(`/packages/${packageId}/signers/${signerId}/reset-idv`);
    },

    /**
     * Reset access code for a signer (sets new code, clears lockout).
     * @param {string} packageId - Package GUID
     * @param {string} signerId - Signer GUID
     * @param {string} newAccessCode - The new access code
     * @returns {Promise<{success: boolean, message?: string}>}
     */
    resetAccessCode(packageId, signerId, newAccessCode) {
        return api.post(`/packages/${packageId}/signers/${signerId}/reset-access-code`, { newAccessCode });
    },

    resetSms(packageId, signerId) {
        return api.post(`/packages/${packageId}/signers/${signerId}/reset-sms`);
    }
};

/**
 * Annotation (Field) Management API
 */
export const annotations = {
    /**
     * List annotations in a package
     * @param {string} packageId - Package GUID
     * @returns {Promise<Array>} List of annotations
     */
    list(packageId) {
        return api.get(`/packages/${packageId}/annotations`);
    },

    /**
     * Get an annotation by ID
     * @param {string} packageId - Package GUID
     * @param {string} annotationId - Annotation GUID
     * @returns {Promise<Object>} Annotation details
     */
    get(packageId, annotationId) {
        return api.get(`/packages/${packageId}/annotations/${annotationId}`);
    },

    /**
     * Create an annotation on a document
     * @param {string} packageId - Package GUID
     * @param {Object} data - Annotation data
     * @param {string} data.documentId - Document GUID
     * @param {string} data.type - Annotation type (Signature, Initials, DateSigned, Text, Checkbox, Dropdown)
     * @param {number} data.signerIndex - Signer index (0-based)
     * @param {number} data.pageNumber - Page number (1-based)
     * @param {number} data.x - X position (0-1 normalized)
     * @param {number} data.y - Y position (0-1 normalized)
     * @param {number} data.width - Width (0-1 normalized)
     * @param {number} data.height - Height (0-1 normalized)
     * @param {boolean} [data.required] - Whether field is required (default: true)
     * @param {string} [data.label] - Field label
     * @param {string} [data.group] - Field group name
     * @param {Array<string>} [data.options] - Options for dropdown fields
     * @param {string} [data.defaultValue] - Default value
     * @param {string} [data.anchorText] - Text to anchor position to
     * @param {number} [data.anchorOffsetX] - Offset from anchor X
     * @param {number} [data.anchorOffsetY] - Offset from anchor Y
     * @returns {Promise<Object>} Created annotation
     */
    create(packageId, data) {
        return api.post(`/packages/${packageId}/annotations`, data);
    },

    /**
     * Create multiple annotations in bulk
     * @param {string} packageId - Package GUID
     * @param {Array} annotations - Array of annotation data objects
     * @returns {Promise<Array>} Created annotations
     */
    createBulk(packageId, annotations) {
        return api.post(`/packages/${packageId}/annotations/bulk`, { annotations });
    },

    /**
     * Update an annotation
     * @param {string} packageId - Package GUID
     * @param {string} annotationId - Annotation GUID
     * @param {Object} data - Fields to update
     * @returns {Promise<Object>} Updated annotation
     */
    update(packageId, annotationId, data) {
        return api.put(`/packages/${packageId}/annotations/${annotationId}`, data);
    },

    /**
     * Delete an annotation
     * @param {string} packageId - Package GUID
     * @param {string} annotationId - Annotation GUID
     * @returns {Promise<void>}
     */
    delete(packageId, annotationId) {
        return api.delete(`/packages/${packageId}/annotations/${annotationId}`);
    },

    /**
     * Validate that all required annotations are complete
     * @param {string} packageId - Package GUID
     * @returns {Promise<{isValid: boolean, errors: Array}>}
     */
    validate(packageId) {
        return api.post(`/packages/${packageId}/annotations/validate`);
    },

    /**
     * Apply detected text tags as annotations for all documents in a package.
     * Idempotent - skips documents that already have annotations.
     * @param {string} packageId - Package GUID
     * @returns {Promise<{annotations: Array, count: number}>}
     */
    applyDetectedTags(packageId) {
        return api.post(`/packages/${packageId}/apply-detected-tags`);
    }
};

/**
 * Template Management API
 */
export const templates = {
    list(params = {}) {
        return api.get('/templates', { params });
    },
    get(templateId) {
        return api.get(`/templates/${templateId}`);
    },
    create(data) {
        return api.post('/templates', data);
    },
    update(templateId, data) {
        return api.put(`/templates/${templateId}`, data);
    },
    delete(templateId) {
        return api.delete(`/templates/${templateId}`);
    },
    bulkDelete(ids) {
        return api.post('/templates/bulk-delete', { ids });
    },
    resetConfiguration(templateId) {
        return api.post(`/templates/${templateId}/reset`);
    },
    listDocuments(templateId) {
        return api.get(`/templates/${templateId}/documents`);
    },
    removeDocument(templateId, documentId) {
        return api.delete(`/templates/${templateId}/documents/${documentId}`);
    },
    getDocumentRemovalImpact(templateId, documentId) {
        return api.get(`/templates/${templateId}/documents/${documentId}/removal-impact`);
    },
    listRoles(templateId) {
        return api.get(`/templates/${templateId}/roles`);
    },
    addRole(templateId, data) {
        return api.post(`/templates/${templateId}/roles`, data);
    },
    updateRole(templateId, roleName, data) {
        return api.put(`/templates/${templateId}/roles/${encodeURIComponent(roleName)}`, data);
    },
    removeRole(templateId, roleName) {
        return api.delete(`/templates/${templateId}/roles/${encodeURIComponent(roleName)}`);
    },
    reorderRoles(templateId, roles) {
        return api.put(`/templates/${templateId}/roles/reorder`, { roles });
    },
    retryDocumentProcessing(templateId, documentId) {
        return api.post(`/templates/${templateId}/documents/${documentId}/retry`);
    },
    getDetectedTags(templateId, documentId) {
        return api.get(`/templates/${templateId}/documents/${documentId}/detected-tags`);
    },
    listAnnotations(templateId, documentId = null) {
        const params = documentId ? { documentId } : {};
        return api.get(`/templates/${templateId}/annotations`, { params });
    },
    addAnnotationsBulk(templateId, documentId, annotations) {
        return api.post(`/templates/${templateId}/documents/${documentId}/annotations/bulk`, { annotations });
    },
    removeAnnotation(templateId, annotationId) {
        return api.delete(`/templates/${templateId}/annotations/${annotationId}`);
    },
    createAnnotation(templateId, documentId, annotation) {
        return api.post(`/templates/${templateId}/documents/${documentId}/annotations/bulk`, { annotations: [annotation] });
    },
    updateAnnotation(templateId, annotationId, data) {
        return api.put(`/templates/${templateId}/annotations/${annotationId}`, data);
    },
    getDocumentPages(templateId, documentId, dpi = 144) {
        return api.get(`/templates/${templateId}/documents/${documentId}/pages`, { params: { dpi } });
    },
    createPackage(templateId, data) {
        return api.post(`/templates/${templateId}/create-package`, data);
    },

    /**
     * Chunked upload methods for large template documents
     */
    chunkedUpload: {
        initiate(templateId, fileName, totalSizeBytes) {
            return api.post(`/templates/${templateId}/uploads`, { fileName, totalSizeBytes });
        },
        stageBlock(templateId, sessionId, blockIndex, blockData) {
            return api.put(
                `/templates/${templateId}/uploads/${sessionId}/blocks/${blockIndex}`,
                blockData,
                { headers: { 'Content-Type': 'application/octet-stream' }, timeout: 120000 }
            );
        },
        commit(templateId, sessionId, options = {}) {
            return api.post(`/templates/${templateId}/uploads/${sessionId}/commit`, options);
        },
        abort(templateId, sessionId) {
            return api.delete(`/templates/${templateId}/uploads/${sessionId}`);
        },
        getStatus(templateId, sessionId) {
            return api.get(`/templates/${templateId}/uploads/${sessionId}`);
        }
    }
};

/**
 * Signing Groups API
 */
export const signingGroups = {
    list(params = {}) {
        return api.get('/signing-groups', { params });
    },
    create(data) {
        return api.post('/signing-groups', data);
    },
    get(groupId) {
        return api.get(`/signing-groups/${groupId}`);
    },
    update(groupId, data) {
        return api.put(`/signing-groups/${groupId}`, data);
    },
    delete(groupId) {
        return api.delete(`/signing-groups/${groupId}`);
    },
    bulkDelete(ids) {
        return api.post('/signing-groups/bulk-delete', { ids });
    },
    listMembers(groupId) {
        return api.get(`/signing-groups/${groupId}/members`);
    },
    addMember(groupId, data) {
        return api.post(`/signing-groups/${groupId}/members`, data);
    },
    removeMember(groupId, email) {
        return api.delete(`/signing-groups/${groupId}/members/${encodeURIComponent(email)}`);
    }
};

/**
 * Branding / Corporate Identity API
 */
/**
 * Auto-Refill API (proxied through Platform.API)
 */
export const autoRefill = {
    /** List all auto-refill settings for the tenant */
    list() {
        return api.get('/subscription/auto-refill');
    },
    /** Create or update auto-refill setting for a credit type */
    upsert(creditType, data) {
        return api.put(`/subscription/auto-refill/${creditType}`, data);
    },
    /** Delete auto-refill setting for a credit type */
    delete(creditType) {
        return api.delete(`/subscription/auto-refill/${creditType}`);
    }
};

export const branding = {
    /**
     * Get current branding settings
     * @returns {Promise<Object>} Branding settings
     */
    get() {
        return api.get('/branding');
    },

    /**
     * Create or update branding settings
     * @param {Object} data - Branding settings
     * @returns {Promise<Object>} Updated branding settings
     */
    update(data) {
        return api.put('/branding', data);
    },

    /**
     * Upload a logo image
     * @param {File} file - Image file (PNG, JPEG, or SVG, max 2 MB)
     * @returns {Promise<{url: string}>}
     */
    uploadLogo(file) {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/branding/logo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    /**
     * Delete the logo image
     * @returns {Promise<void>}
     */
    deleteLogo() {
        return api.delete('/branding/logo');
    },

    /**
     * Upload a favicon image
     * @param {File} file - Image file (PNG or ICO, max 100 KB)
     * @returns {Promise<{url: string}>}
     */
    uploadFavicon(file) {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/branding/favicon', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    /**
     * Delete the favicon image
     * @returns {Promise<void>}
     */
    deleteFavicon() {
        return api.delete('/branding/favicon');
    }
};

/**
 * Webhook Subscription Management API
 */
export const webhooks = {
    /**
     * List webhook subscriptions
     * @returns {Promise<Array>}
     */
    list() {
        return api.get('/webhooks');
    },

    /**
     * Get a webhook subscription
     * @param {string} webhookId - Webhook GUID
     * @returns {Promise<Object>}
     */
    get(webhookId) {
        return api.get(`/webhooks/${webhookId}`);
    },

    /**
     * Create a webhook subscription
     * @param {Object} data - { url, events, description? }
     * @returns {Promise<Object>} Created subscription (includes full secret, shown only once)
     */
    create(data) {
        return api.post('/webhooks', data);
    },

    /**
     * Update a webhook subscription
     * @param {string} webhookId - Webhook GUID
     * @param {Object} data - { url?, events?, description?, isActive? }
     * @returns {Promise<Object>}
     */
    update(webhookId, data) {
        return api.put(`/webhooks/${webhookId}`, data);
    },

    /**
     * Delete a webhook subscription
     * @param {string} webhookId - Webhook GUID
     * @returns {Promise<void>}
     */
    delete(webhookId) {
        return api.delete(`/webhooks/${webhookId}`);
    },

    /**
     * Send a test webhook event
     * @param {string} webhookId - Webhook GUID
     * @returns {Promise<{success: boolean, statusCode: number, responseBody?: string, error?: string, durationMs: number}>}
     */
    test(webhookId) {
        return api.post(`/webhooks/${webhookId}/test`);
    },

    /**
     * Get delivery history for a subscription
     * @param {string} webhookId - Webhook GUID
     * @param {number} [limit=20] - Max deliveries to return
     * @returns {Promise<Array>}
     */
    getDeliveries(webhookId, limit = 20, status = null) {
        const params = { limit };
        if (status) params.status = status;
        return api.get(`/webhooks/${webhookId}/deliveries`, { params });
    },

    /**
     * Get a single delivery with full payload
     * @param {string} webhookId - Webhook GUID
     * @param {string} deliveryId - Delivery GUID
     * @returns {Promise<Object>} Delivery detail including payloadJson
     */
    getDelivery(webhookId, deliveryId) {
        return api.get(`/webhooks/${webhookId}/deliveries/${deliveryId}`);
    },

    /**
     * Retry a dead-lettered delivery
     * @param {string} webhookId - Webhook GUID
     * @param {string} deliveryId - Delivery GUID
     * @returns {Promise<Object>} New delivery
     */
    retryDelivery(webhookId, deliveryId) {
        return api.post(`/webhooks/${webhookId}/deliveries/${deliveryId}/retry`);
    },

    /**
     * Reveal the full signing secret for a subscription
     * @param {string} webhookId - Webhook GUID
     * @returns {Promise<{secret: string}>}
     */
    revealSecret(webhookId) {
        return api.get(`/webhooks/${webhookId}/secret`);
    },

    /**
     * Regenerate the signing secret (invalidates old secret immediately)
     * @param {string} webhookId - Webhook GUID
     * @returns {Promise<{secret: string}>} New secret, shown only once
     */
    regenerateSecret(webhookId) {
        return api.post(`/webhooks/${webhookId}/regenerate-secret`);
    },

    /**
     * List available webhook event types
     * @returns {Promise<Array<{name: string, description: string|null}>>}
     */
    getEventTypes() {
        return api.get('/webhooks/event-types');
    }
};

export const payments = {
    /**
     * List payments with server-side pagination, filtering, search, and sort.
     * @param {Object} params - Query params: page, pageSize, status, search, sortBy, sortDescending, dateFrom, dateTo
     * @returns {Promise<{items: Array, totalCount: number, pageNumber: number, pageSize: number, totalAmount: number, totalRefunded: number}>}
     */
    list(params = {}) {
        return api.get('/payments', { params });
    },

    /**
     * Get detailed payment information including Stripe IDs.
     * @param {string} paymentGuid - Payment GUID
     * @returns {Promise<Object>} Payment detail
     */
    getDetail(paymentGuid) {
        return api.get(`/payments/${paymentGuid}`);
    },

    /**
     * Sync a payment's status from Stripe API.
     * @param {string} paymentGuid - Payment GUID
     * @returns {Promise<Object>} Updated payment detail
     */
    sync(paymentGuid) {
        return api.post(`/payments/${paymentGuid}/sync`);
    },

    /**
     * Refund a package's payment (full or partial).
     * @param {string} packageGuid - Package GUID
     * @param {number|null} amount - Partial refund amount in dollars, null for full refund
     * @param {string|null} reason - Refund reason (requested_by_customer, duplicate, fraudulent)
     * @returns {Promise<{success: boolean, refundedAmount: number, refundId: string|null, errorMessage: string|null}>}
     */
    refund(packageGuid, amount = null, reason = null) {
        return api.post(`/payment-settings/packages/${packageGuid}/refund`, { amount, reason });
    },

    /**
     * Refund a specific payment by payment GUID (full or partial).
     * @param {string} paymentGuid - Payment GUID
     * @param {number|null} amount - Partial refund amount in dollars, null for full refund
     * @param {string|null} reason - Refund reason
     * @returns {Promise<Object>}
     */
    refundPayment(paymentGuid, amount = null, reason = null) {
        return api.post(`/payments/${paymentGuid}/refund`, { amount, reason });
    },

    // ───── Stripe Tax Settings ─────

    /**
     * Fetch Stripe's product tax-code catalogue for UI pickers. Server caches this
     * for 12 hours so calling it on every settings-page load is fine.
     * @returns {Promise<{codes: Array<{id: string, name: string, description: string | null}>}>}
     */
    listTaxCodes() {
        return api.get('/payment-settings/tax-codes');
    },

    /**
     * Read the tenant's default Stripe tax_code + their connect account's Stripe
     * Tax status (active + registration count). Drives the Payment Settings UI:
     * shows current tax code name, "active in 5 US states" status, and a link
     * to the tenant's Stripe Dashboard tax page.
     * @returns {Promise<{
     *   defaultTaxCodeId: string | null,
     *   defaultTaxCodeName: string | null,
     *   stripeTaxActive: boolean,
     *   activeRegistrationCount: number | null,
     *   stripeDashboardTaxUrl: string | null
     * }>}
     */
    getTaxConfig() {
        return api.get('/payment-settings/tax-config');
    },

    /**
     * Record the tenant's affirmative tax-collection election. Writes to
     * SignTenantStripeConnect AND appends a row to Sign_TenantFinancialAuditLog
     * capturing who + when + IP. Server rejects Undeclared.
     * @param {'NotRequired' | 'Enabled'} election
     * @param {string | null} notRequiredReason - Required when election='NotRequired'.
     *   Free-form up to 500 chars. Persisted AND written to the audit row.
     * @returns {Promise<Object>} Updated TaxConfig payload.
     */
    updateTaxElection(election, notRequiredReason = null) {
        return api.put('/payment-settings/tax-election', { election, notRequiredReason });
    }
};

/**
 * Annotation field-type sizing rules. Static product config - fetch once
 * at app load (or first field-editor open) and cache for the session. The
 * server is the single source of truth so the UI can't drift from the
 * server-side resolver's defaults and minimums.
 */
export const annotationFieldTypes = {
    /**
     * Get the per-type sizing rules. Response shape:
     * { fallbackLineHeightFraction: number, types: { Signature: {...}, Initials: {...}, ... } }
     * Each rule: { widthMultiplier, heightMultiplier, minWidthPt, minHeightPt }.
     * Defaults are computed as `lineHeight * multiplier` then clamped to min.
     * @returns {Promise<{fallbackLineHeightFraction: number, types: Object}>}
     */
    get() {
        return api.get('/annotation-field-types');
    }
};

/**
 * Bulk Send API (Phase A native UI).
 *
 * Thin wrapper around the portal proxy at /api/v1/bulk-sends/*. Channel-parity
 * invariant (plan §2): no client-side derivation. The server returns a
 * pre-formatted `displayName`, per-row error codes that the classifier maps
 * to {friendly, retryable, helpHint}, and ETag / Retry-After headers we
 * round-trip via axios's `headers` config. We never reshape the response.
 */
export const bulkSends = {
    /**
     * List bulk-send jobs for the current tenant. Channel-parity: any
     * integration that hits the equivalent Platform.API surface gets the
     * same `displayName` and ordering. The portal never re-sorts or
     * re-filters client-side.
     * @param {{page?: number, pageSize?: number, status?: string}} [params]
     */
    list(params = {}) {
        return api.get('/bulk-sends', { params });
    },

    /**
     * Submit a new bulk-send run.
     * @param {string} templateGuid - Template GUID
     * @param {{recipients: Array}} payload - Submit DTO
     * @param {string} [idempotencyKey] - UUID minted at wizard entry
     */
    submit(templateGuid, payload, idempotencyKey) {
        const headers = {};
        if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
        return api.post(`/bulk-sends/submit/${templateGuid}`, payload, { headers });
    },

    /**
     * Poll status for a bulk-send job. Pass the previous response's ETag
     * via `ifNoneMatch` to get 304 Not Modified when nothing has changed.
     * The 304 case is NOT an error — axios resolves it with status=304 and
     * an empty body. The caller checks `response.status === 304` and skips
     * the data merge.
     * @param {string} jobGuid - Job GUID
     * @param {string} [ifNoneMatch] - Prior ETag value (verbatim, with quotes)
     */
    get(jobGuid, ifNoneMatch) {
        const headers = {};
        if (ifNoneMatch) headers['If-None-Match'] = ifNoneMatch;
        return api.get(`/bulk-sends/${jobGuid}`, {
            headers,
            // 304 must NOT throw — axios treats 3xx as success by default,
            // but we explicitly opt in via validateStatus so it's obvious
            // to future readers what the contract is.
            validateStatus: (s) => (s >= 200 && s < 300) || s === 304
        });
    },

    /**
     * Retry just the transient-failed rows as a new job.
     * @param {string} jobGuid - Original job GUID
     * @param {{resultIds?: number[]}} [payload] - Optional subset (Phase A unused)
     */
    retryFailed(jobGuid, payload) {
        return api.post(`/bulk-sends/${jobGuid}/retry-failed`, payload || {});
    }
};

/**
 * Default export with all API modules
 */
export default {
    packages,
    documents,
    signers,
    annotations,
    annotationFieldTypes,
    templates,
    signingGroups,
    autoRefill,
    branding,
    webhooks,
    payments,
    bulkSends
};
